import { defineRouting } from 'next-intl/routing'

/**
 * Routage multilingue — source de vérité unique des chemins du site.
 *
 * - Le français est la langue par défaut et n'a AUCUN préfixe.
 * - L'anglais est servi sous /en.
 * - Les chemins sont TRADUITS, pas seulement préfixés (/a-propos <-> /en/about).
 *
 * Ajouter l'arabe : ajouter 'ar' à `locales`, une colonne dans `pathnames`,
 * un fichier src/messages/ar.json et une ligne dans la table Languages.
 * Aucun composant n'est à réécrire (propriétés CSS logiques, CLAUDE.md § 8).
 */
export const routing = defineRouting({
  locales: ['fr', 'en'],
  defaultLocale: 'fr',
  localePrefix: 'as-needed',
  localeDetection: false,
  pathnames: {
    '/': '/',
    '/a-propos': { fr: '/a-propos', en: '/about' },
    /*
     * Les domaines n'ont plus de page liste : elle ne faisait que répéter les
     * six cartes de l'accueil. Seules les pages de détail subsistent, et on y
     * entre par le bloc « Nos domaines » de l'accueil.
     */
    '/domaines/[slug]': { fr: '/domaines/[slug]', en: '/industries/[slug]' },
    '/solutions': { fr: '/solutions', en: '/solutions' },
    '/solutions/[slug]': { fr: '/solutions/[slug]', en: '/solutions/[slug]' },
    '/contact': { fr: '/contact', en: '/contact' },
    '/devenir-partenaire': { fr: '/devenir-partenaire', en: '/become-a-partner' },
    '/pfe': { fr: '/pfe', en: '/internships' },
    '/pfe/[slug]': { fr: '/pfe/[slug]', en: '/internships/[slug]' },
    '/mentions-legales': { fr: '/mentions-legales', en: '/legal-notice' },
    '/confidentialite': { fr: '/confidentialite', en: '/privacy-policy' },
  },
})

export type Locale = (typeof routing.locales)[number]
export type AppPathname = keyof typeof routing.pathnames

/**
 * Clés de route utilisées par le back-office : un élément de menu référence une
 * clé, jamais une URL en dur. Le front résout la clé vers le chemin de la langue
 * courante — les URLs restent donc toujours cohérentes.
 *
 * Une clé retirée d'ici peut subsister en base, dans un menu ou un appel à
 * l'action saisi avant son retrait. Le lien est alors IGNORÉ plutôt que rendu
 * vers `undefined` : voir `isKnownRouteKey` ci-dessous.
 */
export const ROUTE_KEYS = {
  home: '/',
  about: '/a-propos',
  solutions: '/solutions',
  contact: '/contact',
  partner: '/devenir-partenaire',
  pfe: '/pfe',
  legalNotice: '/mentions-legales',
  privacy: '/confidentialite',
} as const satisfies Record<string, AppPathname>

export type RouteKey = keyof typeof ROUTE_KEYS

/** Garde d'exécution : la base peut encore porter une clé de route retirée. */
export function isKnownRouteKey(value: string | undefined | null): value is RouteKey {
  return value !== undefined && value !== null && value in ROUTE_KEYS
}
